import { Button } from "devextreme-react/button";
import {
  Column,
  DataGrid,
  Item as DataGridItem,
  FilterRow,
  Toolbar,
} from "devextreme-react/data-grid";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AvailableEmployee2, formatDateOrIndefinite } from "../types";

type Props = {
  employees: AvailableEmployee2[];
  isMobile: boolean;
  search: string;
  onAccept: (row: AvailableEmployee2) => void;
};

const keyOf = (row: { companyId: number; employeeId: number }) =>
  `${row.companyId}-${row.employeeId}`;

export const AvailableEmployeesView2 = memo(function AvailableEmployeesView2({
  employees,
  isMobile,
  search,
  onAccept,
}: Props) {
  const { t } = useTranslation();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      [
        e.surname,
        e.name,
        e.position,
        e.companyName,
        ...(e.projects || []).map((p) => p.name),
      ]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q)),
    );
  }, [employees, search]);

  if (isMobile) {
    if (filtered.length === 0) {
      return (
        <div className="transfer2-empty" role="alert">
          {t("transferOfEmployees2.emptyAvailable")}
        </div>
      );
    }
    return (
      <div className="transfer2-cards">
        {filtered.map((row) => {
          const projects = (row.projects || []).map((p) => p.name).join(", ");
          return (
            <div key={keyOf(row)} className="transfer2-card">
              <div className="transfer2-card-header">
                <div className="transfer2-card-name">
                  {row.surname} {row.name}
                </div>
                {row.position && (
                  <div className="transfer2-card-sub">{row.position}</div>
                )}
                {row.absence && (
                  <span className="transfer2-card-badge-warning">
                    {t("transferOfEmployees.absenceUserInThisTime")}
                  </span>
                )}
              </div>
              <div className="transfer2-card-body">
                {row.companyName && (
                  <div className="transfer2-card-row">
                    <span className="transfer2-card-label">
                      {t("transferOfEmployees.companyName")}:
                    </span>
                    <span>{row.companyName}</span>
                  </div>
                )}
                <div className="transfer2-card-row">
                  <span className="transfer2-card-label">
                    {t("transferOfEmployees2.availableFrom")}:
                  </span>
                  <span>
                    {formatDateOrIndefinite(
                      row.releaseStartDate,
                      t("transferOfEmployees2.indefinite"),
                    )}
                  </span>
                </div>
                <div className="transfer2-card-row">
                  <span className="transfer2-card-label">
                    {t("transferOfEmployees2.availableTo")}:
                  </span>
                  <span>
                    {formatDateOrIndefinite(
                      row.releaseEndDate,
                      t("transferOfEmployees2.indefinite"),
                    )}
                  </span>
                </div>
                {projects && (
                  <div className="transfer2-card-row">
                    <span className="transfer2-card-label">
                      {t("transferOfEmployees.project")}:
                    </span>
                    <span>{projects}</span>
                  </div>
                )}
              </div>
              <div className="transfer2-card-actions">
                <Button
                  icon="check"
                  type="default"
                  stylingMode="contained"
                  text={t("transferOfEmployees2.acceptAction")}
                  width="100%"
                  onClick={() => onAccept(row)}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <DataGrid
      dataSource={filtered}
      keyExpr={["companyId", "employeeId"]}
      showBorders
      allowColumnResizing
      allowColumnReordering
      columnAutoWidth
      noDataText={t("transferOfEmployees2.emptyAvailable")}
      searchPanel={{
        visible: true,
        placeholder: t("transferOfEmployees.Search..."),
      }}
      paging={{ pageSize: 10 }}
      pager={{ visible: true, showInfo: true, showNavigationButtons: true }}
    >
      <Toolbar>
        <DataGridItem
          name="searchPanel"
          location="before"
          cssClass="datagrid-search-panel-without-margin-left"
        />
      </Toolbar>
      <FilterRow visible />
      <Column
        dataField="surname"
        dataType="string"
        caption={t("transferOfEmployees.lastName")}
        minWidth={180}
      />
      <Column
        dataField="name"
        dataType="string"
        caption={t("transferOfEmployees.firstName")}
        minWidth={180}
      />
      <Column
        dataField="companyName"
        dataType="string"
        caption={t("transferOfEmployees.companyName")}
        minWidth={140}
      />
      <Column
        dataField="position"
        dataType="string"
        caption={t("transferOfEmployees.position")}
        minWidth={160}
      />
      <Column
        dataField="releaseStartDate"
        caption={t("transferOfEmployees2.availableFrom")}
        calculateDisplayValue={(d: AvailableEmployee2) =>
          formatDateOrIndefinite(
            d.releaseStartDate,
            t("transferOfEmployees2.indefinite"),
          )
        }
        minWidth={160}
      />
      <Column
        dataField="releaseEndDate"
        caption={t("transferOfEmployees2.availableTo")}
        calculateDisplayValue={(d: AvailableEmployee2) =>
          formatDateOrIndefinite(
            d.releaseEndDate,
            t("transferOfEmployees2.indefinite"),
          )
        }
        minWidth={160}
      />
      <Column
        type="buttons"
        caption={t("transferOfEmployees2.actions")}
        width={140}
        buttons={[
          {
            icon: "errorcircle",
            hint: t("transferOfEmployees.absenceUserInThisTime"),
            visible: (e) => !!e.row?.data?.absence,
          },
          {
            icon: "check",
            hint: t("transferOfEmployees2.acceptAction"),
            onClick: (e) => e.row?.data && onAccept(e.row.data),
          },
        ]}
      />
    </DataGrid>
  );
});
